import {Component, h} from 'preact';
import {GetBrandReviewSummary} from '@services/BrandReviews/query.graphql';
import {DELETE_REVIEW, DELETE_COMMENT, DENY_REVIEW, RESPOND_REVIEW} from './index';
import isTamUser from '@util/fn/isTam';
import Message from '@components/common/Message';
import Spinner from '@components/common/Spinner';
import Modal from '@components/common/Modal';
import ReviewsList from '@components/customerReviews/ReviewsList';
import ReviewSummary from '@components/customerReviews/ReviewSummary';
import Pagination from '@components/customerReviews/Pagination';
import DialogDeleteReview from '@components/customerReviews/DialogDeleteReview';
import DialogDeleteComment from '@components/customerReviews/DialogDeleteComment';
import DialogDenyReview from '@components/customerReviews/DialogDenyReview';
import DialogRespondReview from '@components/customerReviews/DialogRespondReview';
import ReviewFilter from '@components/customerReviews/ReviewFilter';
import Button from '@components/common/Button';
import {getCreateReviewURL} from '@util/URLUtils';

const lblWriteReview = upi.i18n.getTextFromKey( 'lbl-writeReview' );

const CustomerReviews = ( {
                              brandReviewSummary,
                              loading,
                              getPaginationVars,
                              handleFilterChange,
                              handleFiltersClear,
                              handleLoadMore,
                              onEditReview,
                              filters,
                              errors
                          },
                          context ) => {

    const {user, brandId, modals, openModal, closeModal} = context;
    const {lastRequestIndex, requestCount, totalRows, reviews = []} = brandReviewSummary.reviewPagination;
    const cursorPosition = (lastRequestIndex + 1) * requestCount;
    const queryVars = getPaginationVars( {newIndex: lastRequestIndex} );
    const hasPublishedReviews = brandReviewSummary.totalPublishedReviews > 0;
    const hasPaginationResults = reviews && reviews.length > 0;

    const isTam = isTamUser( user );

    return (
        <div className="block w-full">
            <div className="xl:flex">
                <div className="flex-0 w-full md:flex xl:block xl:max-w-sm">
                    <ReviewSummary brandId={queryVars.brandId}
                                   reviewSummary={brandReviewSummary}
                                   onRatingFilterChange={handleFilterChange}/>

                    <div className="leave-review flex flex-1 items-center justify-center mb-4">
                        <div className="flex-1 my-4 lg:mt-8 ">
                            {isTam
                                ? null
                                : <Button
                                    as="a"
                                    fillMobile
                                    data-testid="writeReview"
                                    href={getCreateReviewURL( brandId )}>{lblWriteReview}</Button>
                            }
                        </div>
                    </div>

                </div>
                <div className="flex-1 relative">
                    {loading &&
                    <div className="absolute pin-x my-4 flex justify-center fadeUp">
                        <div className="bg-near-white rounded-full inline-block shadow-1">
                            <Spinner reverse message="Refreshing" padding={2}/>
                        </div>
                    </div>
                    }

                    {user.isReviewModerator || hasPublishedReviews ?
                        <ReviewFilter currentFilters={filters}
                                      onFilterChange={handleFilterChange}
                                      error={errors.filters}
                        />
                        : null
                    }

                    {user.isReviewModerator && !hasPublishedReviews &&
                    <Message className="my-4 bg-yellow-lighter txt-c1 font-bold text-base">
                        There are no published reviews for this product.
                    </Message>
                    }

                    {(hasPublishedReviews || user.isReviewModerator) && !hasPaginationResults ?
                        <div className="w-full my-8">
                            <h1 className="text-lg font-normal">
                                Sorry, there are no reviews for your current selection.
                            </h1>
                            <p>
                                Try changing your filter selection or
                                {' '}
                                <span className="link-1" onClick={handleFiltersClear}>Show all reviews</span>
                            </p>
                        </div>
                        : null
                    }

                    {hasPaginationResults &&
                    <ReviewsList
                        reviews={reviews}
                        filters={filters}
                        isAdmin={user.isReviewModerator}
                        onEditReview={onEditReview}
                        onDeleteReview={( id, reviewId ) => openModal( DELETE_REVIEW, {id, reviewId} )}
                        onDeleteComment={( id ) => openModal( DELETE_COMMENT, {id} )}
                        onRespondReview={( id, comment ) => openModal( RESPOND_REVIEW, {id, comment} )}
                        onDenyReview={id => openModal( DENY_REVIEW, {id} )}/>
                    }

                    <Pagination
                        cursorPosition={cursorPosition}
                        loading={loading}
                        error={errors.pagination}
                        totalCount={totalRows}
                        onLoadMore={() => handleLoadMore( lastRequestIndex + 1 )}/>
                </div>
            </div>

            <Modal
                title="Reason for denial"
                id={DENY_REVIEW}
                onClose={closeModal}
                isOpen={modals[DENY_REVIEW].isOpen}>
                <DialogDenyReview submitData={modals[DENY_REVIEW].props}/>
            </Modal>
            <Modal
                title="Respond to customer"
                id={RESPOND_REVIEW}
                onClose={closeModal}
                isOpen={modals[RESPOND_REVIEW].isOpen}>
                <DialogRespondReview submitData={modals[RESPOND_REVIEW].props}/>
            </Modal>
            <Modal
                title="Are you sure you want to delete your comment?"
                id={DELETE_COMMENT}
                onClose={closeModal}
                isOpen={modals[DELETE_COMMENT].isOpen}>
                <DialogDeleteComment submitData={modals[DELETE_COMMENT].props}/>
            </Modal>
            <Modal
                title="Are you sure you want to delete your review?"
                id={DELETE_REVIEW}
                onClose={closeModal}
                isOpen={modals[DELETE_REVIEW].isOpen}>
                <DialogDeleteReview
                    refetchQuery={{
                        query: GetBrandReviewSummary,
                        variables: queryVars
                    }}
                    submitData={modals[DELETE_REVIEW].props}/>
            </Modal>
        </div>
    );
};

export default CustomerReviews;